import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { UtensilsCrossed, Apple, Clock, ShieldCheck, Zap } from 'lucide-react';
interface SlideSnakQualityProps {
  onContinue: () => void;
}
const MEAL = ['40-slide approved deck', '12-page clinical reprint', '60-minute webinar', '8-minute e-detailer'];
const SNAK = ['60–90 seconds, start to finish', 'One message, one clear action', 'Interactive and mobile-first', 'Finished on the ward, not saved for later'];
const QUALITY = [{
  icon: ShieldCheck,
  title: 'Same claims, same science',
  desc: 'Nothing outside what has already been approved.'
}, {
  icon: Apple,
  title: 'Distilled, not diluted',
  desc: 'The substance survives. The padding does not.'
}, {
  icon: Zap,
  title: 'Every second earns the next',
  desc: 'Attention is the scarce resource, so nothing is filler.'
}];
export function SlideSnakQuality({
  onContinue
}: SlideSnakQualityProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 900, 1600, 2400].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-1137" data-mp="120nidua68ez7">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-1138" data-mp="xz23k3e2tqmd">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1139" data-mp="o9igsmn8tcm8">
          The Snakkerfy Format
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1140" data-mp="j94j9yj9pl2g">
          So what exactly is a Snakkerfied campaign?
        </h2>
        <p className="text-lg text-white/60" data-id="element-1141" data-mp="aa9colcba8ym">
          No doctor has time for a three-course meal — not even the French. HCPs
          don't lack information, they lack time.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-6xl mx-auto w-full mt-6 space-y-5" data-id="element-1142" data-mp="3rsoo7y35e5ez">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-5 items-stretch" data-id="element-1143" data-mp="3vv1t6ka2lzap">
          {/* The three-course meal */}
          <motion.div initial={{
          opacity: 0,
          x: -18
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -18
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6 flex flex-col" data-id="element-1144" data-mp="3hwlfg9dsj8j2">
            <div className="flex items-center gap-3 mb-4" data-id="element-1145" data-mp="4oiakixzt51u">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center shrink-0" data-id="element-1146" data-mp="4nz2rv223x1j">
                <UtensilsCrossed className="w-5 h-5" data-id="element-1147" data-mp="9liiz0ibejw4" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-1148" data-mp="xbl755j3z5pg">
                The three-course meal
              </h3>
            </div>
            <ul className="space-y-2.5 flex-1" data-id="element-1149" data-mp="2mubc8e2ybbkq">
              {MEAL.map((item, idx) => <motion.li key={item} initial={{
              opacity: 0,
              x: -8
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              x: stage >= 2 ? 0 : -8
            }} transition={{
              delay: stage >= 2 ? idx * 0.08 : 0
            }} className="flex items-start gap-3 text-white/80 text-sm" data-id="element-1150" data-mp="2mrykxabazov0" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" data-id="element-1151" data-mp="2cw3u3rwxenh5" />
                  {item}
                </motion.li>)}
            </ul>
            <p className="text-white/50 text-sm mt-5 pt-4 border-t border-navy-700 leading-relaxed" data-id="element-1152" data-mp="27z567r3uvcre">
              Opened, skimmed, saved for later — and never returned to.
            </p>
          </motion.div>

          {/* Divider */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: stage >= 2 ? 1 : 0
        }} className="flex md:flex-col items-center justify-center gap-3" data-id="element-1153" data-mp="ihhxedwtjhpr">
            <div className="hidden md:block w-px flex-1 bg-navy-600" data-id="element-1154" data-mp="sdusng80i3mm" />
            <span className="px-3 py-1.5 rounded-full bg-teal-400/10 border border-teal-400/30 text-teal-400 text-xs font-semibold tracking-wide uppercase whitespace-nowrap flex items-center gap-1.5" data-id="element-1155" data-mp="xf0klbtulmv3">
              <Clock className="w-3.5 h-3.5" data-id="element-1156" data-mp="16dm9rptts04u" />
              Same nutrition
            </span>
            <div className="hidden md:block w-px flex-1 bg-navy-600" data-id="element-1157" data-mp="1b6oynknnqlep" />
          </motion.div>

          {/* The Snak */}
          <motion.div initial={{
          opacity: 0,
          x: 18
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          x: stage >= 2 ? 0 : 18
        }} className="bg-teal-400/[0.07] border border-teal-400/40 rounded-2xl p-6 flex flex-col" data-id="element-1158" data-mp="1g3ytjfttfp9x">
            <div className="flex items-center gap-3 mb-4" data-id="element-1159" data-mp="35m42f8ncnyv3">
              <div className="w-10 h-10 rounded-xl bg-teal-400/15 text-teal-400 flex items-center justify-center shrink-0" data-id="element-1160" data-mp="35kkkzepyjhrq">
                <Apple className="w-5 h-5" data-id="element-1161" data-mp="30om1a4fkqnxn" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-1162" data-mp="2vp94aquzo0hc">
                The Snakkerfy format
              </h3>
            </div>
            <ul className="space-y-2.5 flex-1" data-id="element-1163" data-mp="3ocderbcup70i">
              {SNAK.map((item, idx) => <motion.li key={item} initial={{
              opacity: 0,
              x: 8
            }} animate={{
              opacity: stage >= 3 ? 1 : 0,
              x: stage >= 3 ? 0 : 8
            }} transition={{
              delay: stage >= 3 ? idx * 0.08 : 0
            }} className="flex items-start gap-3 text-white/85 text-sm" data-id="element-1164" data-mp="3oepudhv4z5ae" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-1.5 shrink-0" data-id="element-1165" data-mp="29f9esmu96r50" />
                  {item}
                </motion.li>)}
            </ul>
            <p className="text-white/60 text-sm mt-5 pt-4 border-t border-teal-400/20 leading-relaxed" data-id="element-1166" data-mp="1vnm95wef869k">
              Finished, remembered, acted on.
            </p>
          </motion.div>
        </div>

        {/* Quality guarantees */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 4 ? 1 : 0
      }} className="text-white/40 text-[11px] font-semibold uppercase tracking-widest" data-id="element-1167" data-mp="1qojf16zzbkgd">
          The three rules of the format
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="grid grid-cols-1 md:grid-cols-3 gap-4 !mt-3" data-id="element-1168" data-mp="372hxyb6s7o3u">
          {QUALITY.map(item => {
          const Icon = item.icon;
          return <div key={item.title} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-5" data-id="element-1169" data-mp="3uycu33msasb0">
                <div className="w-9 h-9 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center mb-3" data-id="element-1170" data-mp="3q6uv2l4bn1so">
                  <Icon className="w-4 h-4" data-id="element-1171" data-mp="8t971oaifmys" />
                </div>
                <p className="text-white font-semibold text-sm mb-1" data-id="element-1172" data-mp="3r0egt1jumx7">
                  {item.title}
                </p>
                <p className="text-white/60 text-sm leading-relaxed" data-id="element-1173" data-mp="hr1oaxzsl4ot">
                  {item.desc}
                </p>
              </div>;
        })}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl px-7 py-5" data-id="element-1174" data-mp="2jjzaiaz2cz0s">
          <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-1175" data-mp="390cclctw4blu">
            To Snakkerfy is to keep the nutrition and drop the courses.{' '}
            <span className="text-teal-400 font-semibold" data-id="element-1176" data-mp="3htpf96hrarl4">
              Short, rich, to the point
            </span>{' '}
            — the same science, served in the time a doctor actually has. Short
            is not less.
          </p>
        </motion.div>
      </div>
    </div>;
}